import posthog from 'posthog-js';
import { stripTokenParam } from '../utils/resetToken';
import { enqueueEvent, entityFields } from './eventMirror';

/**
 * Canonical product-analytics events — the single source of truth for event
 * names so instrumentation stays consistent and typo-free across the app.
 * Full taxonomy + KPI mapping: docs/plans/2026-06-16-analytics-observability-plan.md §2.4
 */
export type AnalyticsEvent =
  // Lifecycle / auth
  | 'user_signed_up'
  | 'email_verified'
  | 'user_signed_in'
  | 'user_signed_out'
  | 'session_expired'
  | 'account_deleted'
  | 'password_changed'
  | 'password_reset_requested'
  | 'password_reset_completed'
  // Navigation. PostHog captures its own $pageview; this is the name the client
  // mirror sends to the backend event log, which has no equivalent of its own.
  | 'page_viewed'
  // Activation — search creation
  | 'search_drawer_opened'
  | 'resume_uploaded'
  | 'resume_review_completed'
  | 'resume_review_skipped'
  | 'search_created'
  | 'search_creation_abandoned'
  | 'coverage_plan_generated'
  | 'search_pack_created'
  | 'search_status_changed'
  | 'search_deleted'
  // Core value — opportunities → application
  | 'opportunity_viewed'
  | 'application_submitted'
  | 'opportunity_rejected'
  | 'opportunity_rejection_undone'
  | 'contact_retry_initiated'
  // Application progression
  | 'application_status_changed'
  | 'application_archived'
  | 'application_unarchived'
  // AI engagement features
  | 'cover_letter_regenerated'
  | 'follow_up_sent'
  | 'reply_sent'
  | 'interview_kit_generated'
  | 'draft_language_selected'
  // Email / integration
  | 'gmail_connected'
  | 'gmail_disconnected'
  | 'gmail_consent_primer_shown'
  | 'gmail_consent_primer_continue'
  | 'gmail_consent_primer_cancel'
  | 'forwarding_address_copied'
  | 'forwarding_notify_mode_changed'
  // Settings — search preferences + Auto-Apply (design §10/§12/§13)
  | 'geo_coverage_changed'
  | 'min_fit_level_changed'
  | 'daily_opportunity_cap_changed'
  | 'auto_apply_activated'
  | 'auto_apply_settings_saved'
  | 'auto_apply_turned_off'
  | 'auto_apply_offer_shown'
  | 'auto_apply_offer_dismissed'
  | 'auto_apply_offer_cta_clicked'
  | 'auto_apply_setup_opened'
  // Auto-Apply cannot be switched on without a Gmail grant that can send. This
  // funnel is gate -> primer -> Google -> resume; the primer's own events already
  // exist, so these three cover the ends of it.
  | 'auto_apply_gmail_gate_shown'
  | 'auto_apply_gmail_gate_connect_clicked'
  | 'auto_apply_gmail_gate_dismissed'
  | 'auto_apply_setup_resumed_after_gmail'
  // Widen-search strip: point-of-need Search Preferences discovery on a thin
  // opportunities list (design: settings discovery, 2026-07-26)
  | 'widen_search_shown'
  | 'widen_search_applied'
  | 'widen_search_dismissed'
  | 'forwarded_email_attached'
  | 'reply_capture_prompt_shown'
  | 'reply_capture_prompt_dismissed'
  | 'email_addon_install_clicked'
  // Notifications
  | 'notification_clicked'
  | 'notification_dismissed'
  // Persistence / encouragement coaching
  | 'apply_reassurance_shown'
  | 'apply_reassurance_dismissed'
  | 'rejection_encouragement_shown'
  // First-run "why your resume looks different" — props: { surface }
  | 'resume_rebuild_explainer_shown'
  // Resume tailoring — editor loop
  | 'resume_tailor_opened'
  | 'resume_fix_accepted'
  | 'resume_comment_added'
  | 'resume_comment_retried'
  | 'resume_remove_requested'
  | 'resume_section_removed'
  | 'resume_structured_undo'
  // Apply/undo that resolved to nothing — the card said so and recorded no
  // decision. Props: { sectionId, op }. These are product failures the user
  // sees, so they must not be silent in PostHog just because the honest path
  // records nothing else.
  | 'resume_fix_apply_failed'
  | 'resume_undo_failed'
  // Structural entry editing — the direct form for a job or school, the only
  // way to change a date or add a job at all. Props: { kind, op }, where kind
  // is experience|education and op is add|edit. The failed twin fires when the
  // save resolved to nothing (the entry was removed underneath it) — same
  // reasoning as the two above: the user saw a failure, so it must not be
  // silent. Its `op` is always 'edit'; an add cannot resolve to nothing.
  | 'resume_entry_saved'
  | 'resume_entry_save_failed'
  | 'resume_version_approved'
  // Resume tailoring — drawer handoff + auto-delta glance
  | 'resume_tailor_entered'
  | 'auto_tailor_confirmed'
  | 'auto_tailor_adjusted'
  | 'auto_tailor_skipped'
  | 'auto_tailor_failed'
  // Warm referral — discover / ask / record
  | 'linkedin_connections_clicked'
  | 'intro_request_opened'
  | 'intro_request_generated'
  | 'intro_request_copied'
  | 'intro_request_draft_failed'
  | 'declare_application_opened'
  | 'application_declared'
  | 'linkedin_stage_opened'
  | 'opportunity_resume_downloaded'
  // Dashboard — human-pull redesign
  | 'dashboard_hero_expanded'
  | 'dashboard_hero_collapsed'
  | 'dashboard_station_clicked'
  | 'dashboard_next_action_clicked'
  // Reliability — product-impacting (mirrored from Sentry where useful)
  | 'llm_generation_failed'
  | 'gmail_send_failed'
  | 'resume_review_unavailable'
  | 'gmail_auth_expired'
  // Turnstile's script never loaded, so this user never reached the backend at
  // all — the API's rejection metrics cannot see them by construction.
  // Props: { reason: 'script_error' | 'load_timeout' | 'render_failed', action }
  | 'turnstile_blocked'
  // The backend rejected a token (403 turnstile_verification_failed). The pair
  // with turnstile_blocked covers both ends of the failure surface, and this is
  // the ONLY quantitative signal for the enforcement rollout: the API suppresses
  // this error in Sentry and so do the token-carrying calls in services/api.ts,
  // deliberately — what the switch-on needs is a rate, not a count.
  // Props: { error_code, endpoint } — endpoint separates signup from login and
  // singles out the exempt app-credential login.
  | 'turnstile_rejected'
  // Admin
  | 'admin_impersonation_started'
  // The closing bracket of the pair above. Without it the activity log states
  // when an admin entered someone's account and never when they left, so every
  // impersonation reads as still open and its extent is unknowable.
  // Captured AFTER the token is swapped back, so it is attributed to the admin
  // rather than stamped `impersonated_by` -- see stopImpersonating() in
  // utils/authUtils.ts for the ordering that buys that.
  | 'admin_impersonation_stopped'
  // Forward-looking revenue (defined now, fired when billing ships)
  | 'paywall_viewed'
  | 'upgrade_cta_clicked'
  | 'subscription_started'
  | 'subscription_changed'
  | 'subscription_canceled'
  | 'payment_succeeded'
  | 'payment_failed';

type Props = Record<string, unknown>;

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;

/** Admin impersonation must never pollute product analytics. */
export const isImpersonating = (): boolean => !!localStorage.getItem('impersonatedUsername');

let initialized = false;

export function initAnalytics(): void {
  if (initialized || !POSTHOG_KEY || isImpersonating()) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change', // SPA-aware: fires on React Router navigations
    // Privacy-first: mask ALL inputs and ALL rendered text in replays (resumes,
    // emails, names). Selectively unmask safe, non-PII elements later for richer replays.
    session_recording: { maskAllInputs: true, maskTextSelector: '*' },
    // Defense-in-depth: strip a password-reset `token` from any captured URL prop
    // (the startup scrub in resetToken.ts is the primary guard; this catches any path
    // that still carries it, e.g. $referrer).
    sanitize_properties: (properties) => {
      for (const key of ['$current_url', '$referrer', '$pathname']) {
        const v = properties[key];
        if (typeof v === 'string') properties[key] = stripTokenParam(v);
      }
      return properties;
    },
  });

  posthog.register({
    environment: process.env.REACT_APP_ENV ?? 'development',
    app_release: process.env.REACT_APP_RELEASE ?? 'dev',
  });

  initialized = true;
}

/** Link subsequent events to a known user. Call after login / verify / on boot. */
export function identifyUser(userId: string, props?: Props): void {
  if (!initialized || isImpersonating()) return;
  posthog.identify(userId, props);
}

/** Set person-level properties. `setOnce` only writes if the property is unset. */
export function setPersonProps(set?: Props, setOnce?: Props): void {
  if (!initialized || isImpersonating()) return;
  posthog.setPersonProperties(set, setOnce);
}

/**
 * Record one product-analytics event in BOTH sinks.
 *
 * The order matters and the two guards are different on purpose. The activity
 * log mirror runs FIRST and unconditionally, because it does not share
 * PostHog's reasons to opt out:
 *
 * - `initialized` is false whenever `REACT_APP_POSTHOG_KEY` is unset (local
 *   dev, and any build whose telemetry vars did not make it into the deploy
 *   workflow). The activity log is a first-party feature of this product and
 *   works with no third-party key at all.
 * - `isImpersonating()` keeps an admin driving someone else's account out of
 *   product analytics, where it would corrupt funnels attributed to that user.
 *   The activity log wants those rows: the impersonation token carries an
 *   `act` claim and the server tags each row `impersonated_by`, so the console
 *   can tell staff activity from the user's own — which PostHog cannot.
 *
 * `enqueueEvent` therefore sits ABOVE the early return, not inside it.
 */
export function captureEvent(event: AnalyticsEvent, props?: Props): void {
  enqueueEvent({ name: event, properties: props, ...entityFields(props) });
  if (!initialized || isImpersonating()) return;
  posthog.capture(event, props);
}

/** Clear identity on logout / session expiry so events aren't misattributed. */
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}

export { posthog };
